import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FileText, Brain, TrendingUp, Zap, Globe, Layers,
  Sparkles, WifiOff, Check, BookOpen,
} from 'lucide-react'

import { APP_TRANSLATIONS } from '../i18n/app'
import { useLocale } from '../i18n/context'
import { detectLocale, LANDING_LOCALE_KEY, type LandingLocale } from '../i18n/landing'
import { selectableInterfaceLanguages, useLanguageRegistry, type RegistryLanguage } from '../i18n/registry'
import { useLoadedLocale } from '../i18n/useLoadedLocale'
import { DemoWidget } from '../components/landing/DemoWidget'

// ─── Icon map ────────────────────────────────────────────────────────────────

const ICONS: Record<string, React.ReactNode> = {
  'file-text': <FileText size={20} />,
  brain: <Brain size={20} />,
  'trending-up': <TrendingUp size={20} />,
  zap: <Zap size={20} />,
  globe: <Globe size={20} />,
  layers: <Layers size={20} />,
  sparkles: <Sparkles size={20} />,
  'wifi-off': <WifiOff size={20} />,
}

const STEP_ICONS: Record<string, React.ReactNode> = {
  'file-text': <FileText size={28} />,
  brain: <Brain size={28} />,
  'trending-up': <TrendingUp size={28} />,
}

// ─── Fade-in animation wrapper ────────────────────────────────────────────────

function FadeUp({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  )
}

// ─── Language switcher ────────────────────────────────────────────────────────

function LocaleSwitcher({
  locale,
  options,
  onChange,
}: {
  locale: LandingLocale
  options: RegistryLanguage[]
  onChange: (l: LandingLocale) => void
}) {
  // One language offers no choice.
  if (options.length < 2) return null
  return (
    <div style={{
      display: 'flex',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 50,
      overflow: 'hidden',
    }}>
      {options.map(({ code: l }) => (
        <button
          key={l}
          onClick={() => onChange(l)}
          style={{
            padding: '5px 12px',
            background: locale === l ? 'var(--accent-fill)' : 'transparent',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontFamily: 'var(--font-ui)',
            transition: 'background 0.15s',
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function LandingPage() {
  const registry = useLanguageRegistry()
  const options = selectableInterfaceLanguages(registry)
  // A choice made on this visit wins; until then, the saved choice or the
  // browser language among the offered ones (main.tsx has loaded both the
  // registry and that table before the first paint).
  const [chosen, setChosen] = useState<LandingLocale | null>(null)
  const locale = useLoadedLocale(chosen ?? detectLocale(registry))
  const t = APP_TRANSLATIONS[locale] ?? APP_TRANSLATIONS.en

  const { claimDocumentLocale } = useLocale()
  useEffect(() => {
    claimDocumentLocale(locale)
    return () => claimDocumentLocale(null)
  }, [locale, claimDocumentLocale])

  function handleLocaleChange(l: LandingLocale) {
    setChosen(l)
    localStorage.setItem(LANDING_LOCALE_KEY, l)
  }

  return (
    <div style={{
      background: 'var(--bg-page)',
      color: 'var(--text-primary)',
      fontFamily: 'var(--font-ui)',
      minHeight: '100vh',
    }}>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'var(--bg-header)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 24px',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
            <div style={{
              width: 32,
              height: 32,
              background: 'var(--accent-fill)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <BookOpen size={16} color="#fff" />
            </div>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.02em',
            }}>
              CardSmith
            </span>
          </div>

          <LocaleSwitcher locale={locale} options={options} onChange={handleLocaleChange} />

          <Link
            to="/login"
            style={{
              color: 'rgba(255,255,255,0.75)',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
              padding: '6px 4px',
              transition: 'color 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {t.nav.signIn}
          </Link>

          <Link
            to="/register"
            style={{
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              padding: '8px 18px',
              borderRadius: 50,
              whiteSpace: 'nowrap',
              transition: 'background 0.15s, transform 0.15s',
            }}
            onMouseEnter={e => {
              ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--accent-hover)'
              ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={e => {
              ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--accent-fill)'
              ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)'
            }}
          >
            {t.nav.getStarted}
          </Link>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '80px 24px 96px',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 420px)',
        gap: 64,
        alignItems: 'center',
      }}
        className="landing-hero"
      >
        {/* Left — text */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <span style={{
              display: 'inline-block',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              background: 'var(--accent-soft)',
              padding: '5px 12px',
              borderRadius: 50,
              marginBottom: 24,
            }}>
              {t.hero.badge}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.07 }}
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              fontWeight: 700,
              lineHeight: 1.12,
              letterSpacing: '-0.03em',
              color: 'var(--text-primary)',
              marginBottom: 20,
              whiteSpace: 'pre-line',
            }}
          >
            {t.hero.headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.14 }}
            style={{
              fontSize: 17,
              lineHeight: 1.65,
              color: 'var(--text-secondary)',
              marginBottom: 36,
              maxWidth: 480,
            }}
          >
            {t.hero.sub}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 48 }}
          >
            <Link
              to="/register"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--accent-fill)',
                color: 'var(--text-on-accent)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                padding: '13px 28px',
                borderRadius: 50,
                transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s',
                boxShadow: '0 4px 14px rgba(74,109,140,0.3)',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLAnchorElement
                el.style.background = 'var(--accent-hover)'
                el.style.transform = 'translateY(-2px)'
                el.style.boxShadow = '0 6px 20px rgba(74,109,140,0.4)'
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLAnchorElement
                el.style.background = 'var(--accent-fill)'
                el.style.transform = 'translateY(0)'
                el.style.boxShadow = '0 4px 14px rgba(74,109,140,0.3)'
              }}
            >
              {t.hero.ctaPrimary}
              <span style={{ fontSize: 18, lineHeight: 1 }}>→</span>
            </Link>

            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                color: 'var(--text-secondary)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500,
                padding: '13px 24px',
                borderRadius: 50,
                border: '1px solid var(--border)',
                transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLAnchorElement
                el.style.borderColor = 'var(--accent)'
                el.style.color = 'var(--accent)'
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLAnchorElement
                el.style.borderColor = 'var(--border)'
                el.style.color = 'var(--text-secondary)'
              }}
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}
          >
            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((stat, i) => (
              <div key={i}>
                <div style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: 'var(--accent)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — live demo widget */}
        <div className="landing-demo-col">
          <DemoWidget locale={locale} t={t} />
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px' }}>
          <FadeUp>
            <h2 style={{
              fontSize: 'clamp(24px, 3vw, 36px)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              textAlign: 'center',
              marginBottom: 56,
            }}>
              {t.howItWorks.title}
            </h2>
          </FadeUp>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 24,
          }}>
            {t.howItWorks.steps.map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.1}>
                <div style={{
                  background: 'var(--bg-page)',
                  border: '1px solid var(--border)',
                  borderRadius: 16,
                  padding: 28,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {/* Large number watermark */}
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    right: 16,
                    fontSize: 80,
                    fontWeight: 800,
                    color: 'var(--accent)',
                    opacity: 0.06,
                    lineHeight: 1,
                    fontFamily: 'var(--font-ui)',
                    letterSpacing: '-0.04em',
                    pointerEvents: 'none',
                  }}>
                    {step.n}
                  </div>

                  {/* Icon */}
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'var(--accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent)',
                    marginBottom: 16,
                  }}>
                    {STEP_ICONS[step.icon]}
                  </div>

                  {/* Step number badge */}
                  <div style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: 'var(--accent)',
                    textTransform: 'uppercase',
                    marginBottom: 8,
                  }}>
                    {step.n}
                  </div>

                  <h3 style={{
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    marginBottom: 10,
                    color: 'var(--text-primary)',
                  }}>
                    {step.title}
                  </h3>

                  <p style={{
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'var(--text-secondary)',
                  }}>
                    {step.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <h2 style={{
                fontSize: 'clamp(24px, 3vw, 36px)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                marginBottom: 10,
              }}>
                {t.features.title}
              </h2>
              <p style={{ fontSize: 16, color: 'var(--text-secondary)' }}>
                {t.features.subtitle}
              </p>
            </div>
          </FadeUp>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}>
            {t.features.items.map((item, i) => (
              <FadeUp key={item.title} delay={(i % 3) * 0.08}>
                <div
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 16,
                    padding: 24,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLDivElement
                    el.style.boxShadow = 'var(--shadow-float)'
                    el.style.transform = 'translateY(-2px)'
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLDivElement
                    el.style.boxShadow = 'none'
                    el.style.transform = 'translateY(0)'
                  }}
                >
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent)',
                    marginBottom: 14,
                  }}>
                    {ICONS[item.icon]}
                  </div>

                  <h3 style={{
                    fontSize: 15,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.01em',
                  }}>
                    {item.title}
                  </h3>

                  <p style={{
                    fontSize: 14,
                    lineHeight: 1.65,
                    color: 'var(--text-secondary)',
                  }}>
                    {item.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pro plan ─────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 520, margin: '0 auto', padding: '80px 24px' }}>
          <FadeUp>
            <div style={{
              background: 'var(--bg-page)',
              border: '1px solid var(--border)',
              borderRadius: 24,
              overflow: 'hidden',
              boxShadow: 'var(--shadow-float)',
            }}>
              {/* Pro header bar */}
              <div style={{
                background: 'var(--accent-fill)',
                padding: '20px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div>
                  <span style={{
                    display: 'inline-block',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    padding: '2px 10px',
                    borderRadius: 50,
                    marginBottom: 6,
                  }}>
                    {t.pro.badge}
                  </span>
                  <h2 style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '-0.02em',
                  }}>
                    {t.pro.title}
                  </h2>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: 40,
                    fontWeight: 800,
                    color: '#fff',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}>
                    {t.pro.price}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                    {t.pro.per}
                  </div>
                </div>
              </div>

              {/* Pro body */}
              <div style={{ padding: '28px 32px' }}>
                <p style={{
                  fontSize: 14,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6,
                  marginBottom: 24,
                }}>
                  {t.pro.desc}
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {t.pro.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: 'var(--accent-soft)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent)',
                        flexShrink: 0,
                        marginTop: 1,
                      }}>
                        <Check size={12} strokeWidth={2.5} />
                      </div>
                      <span style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5 }}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    background: 'var(--accent-fill)',
                    color: 'var(--text-on-accent)',
                    textDecoration: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    padding: '13px 24px',
                    borderRadius: 50,
                    transition: 'background 0.15s, transform 0.15s',
                    marginBottom: 14,
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLAnchorElement
                    el.style.background = 'var(--accent-hover)'
                    el.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLAnchorElement
                    el.style.background = 'var(--accent-fill)'
                    el.style.transform = 'translateY(0)'
                  }}
                >
                  {t.pro.cta}
                </Link>

                <p style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}>
                  {t.pro.note}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer style={{
        background: 'var(--bg-header)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '40px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 20,
        }}>
          {/* Logo + tagline */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{
                width: 26,
                height: 26,
                background: 'var(--accent-fill)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <BookOpen size={13} color="#fff" />
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
                CardSmith
              </span>
            </div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
              {t.footer.tagline}
            </p>
          </div>

          {/* Nav links */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { to: '/login', label: t.footer.links.signIn },
              { to: '/register', label: t.footer.links.register },
              { to: '/explore', label: t.footer.links.explore },
              { to: '/pro', label: t.footer.links.pro },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.9)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.5)' }}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* Copyright + locale switcher */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <LocaleSwitcher locale={locale} options={options} onChange={handleLocaleChange} />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
              {t.footer.copy}
            </span>
          </div>
        </div>
      </footer>

      {/* ── Responsive override styles ───────────────────────────────────── */}
      <style>{`
        @media (max-width: 768px) {
          .landing-hero {
            grid-template-columns: 1fr !important;
            padding-top: 48px !important;
            padding-bottom: 64px !important;
            gap: 40px !important;
          }
        }
        @media (max-width: 480px) {
          .landing-demo-col {
            margin: 0 -4px;
          }
        }
        @keyframes demo-spin {
          to { transform: rotate(360deg); }
        }
        .demo-spinner {
          animation: demo-spin 0.75s linear infinite;
        }
        @keyframes demo-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
        .demo-skeleton-pulse {
          animation: demo-pulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
